import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { ReactNode, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import Box from 'ui-box';
import { useDebouncedCallback } from 'use-debounce/lib';
import { majorScale } from '.';
import { IVatRate, IVatRateSnapshotOut, useStores } from '../stores';
import { IPaginationFilter } from '../stores/utils/modelWithCRUD';
import { ISelectProps, Select } from './Select';

export interface IVatRateSelectProps {
  children?: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  title?: string;
  onSelect: (item: IVatRate) => void;
  selected?: IVatRate['id'];
  filter?: IPaginationFilter<IVatRateSnapshotOut>['filter'];
  small?: boolean;
  placement?: ISelectProps<IVatRate>['placement'];
}

export const VatRateSelect: React.FC<IVatRateSelectProps> = observer(
  ({
    children,
    placeholder = '',
    title,
    selected = null,
    onSelect,
    filter = {},
    disabled = false,
    small,
    placement,
  }) => {
    const [opened, setOpened] = useState(false);
    const { vatRateStore } = useStores();

    const [innerSelected, setInnerSelected] = useState<IVatRate | null>(null);

    useEffect(() => {
      let isSub = true;
      if (selected) {
        vatRateStore
          .getItem(selected)
          .then((comp) => isSub && setInnerSelected(comp));
      }
      return () => {
        isSub = false;
      };
    }, [vatRateStore, selected]);

    const [items, setItems] = useState<IVatRate[]>();
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebouncedCallback(setQuery, 150);

    useEffect(() => {
      if (!opened) return;
      let isSub = true;
      vatRateStore
        .fetchMore({ filter: { ...filter, search: query } })
        .then((res) => {
          isSub && setItems(res.items);
        });

      return () => {
        isSub = false;
      };
    }, [vatRateStore, filter, query, opened]);

    return (
      <Select
        title={title}
        marginBottom={0}
        placeholder={placeholder}
        disabled={disabled}
        isOpen={opened}
        onOpen={() => setOpened(true)}
        onClose={() => setOpened(false)}
        small={small}
        placement={placement}
        onQueryChange={
          // We should search the companies .. either by api or locally?
          // a) By API if we could more than a couple of hundreds
          // b) Locally of we will not have more than a couple of hundreds
          // => Companies could be more than hundreds...
          debouncedQuery.callback
        }
        items={items}
        isLoading={!items || vatRateStore.isLoading}
        resolveSelectedValue={(id) => vatRateStore.getItem(id)}
        itemRenderer={(item) => (
          <>
            <p>{item.code}</p>

            <Box
              className="p muted small font-mono"
              marginLeft="auto"
              textAlign="right"
              paddingLeft={majorScale(1)}
            >
              {item.display_name}
              {': '}
              <span className="font-mono">
                <FormattedNumber format="percent" value={item.rate} />
              </span>
            </Box>
          </>
        )}
        onSelect={(item) => {
          setInnerSelected(item);
          onSelect && onSelect(item);
        }}
        value={innerSelected ? innerSelected.id : undefined}
        valueRenderer={(selected) => selected.code}
      >
        {children}
      </Select>
    );
  }
);
